/**
 * Loop Widget – Points Display Injector
 *
 * For every .loop-widget-purchase-option-label:
 *  - Wraps it + a new points div inside a shared parent div
 *  - Reads the active price (sale price if compare-at exists, else regular)
 *  - Displays points = price in dollars (e.g. $49.99 → 49 points)
 *
 * Points formula: floor(price in dollars) — adjust POINTS_PER_DOLLAR below.
 */

(function () {
  const POINTS_PER_DOLLAR = 1; // 1 point per $1 spent — change as needed
  const WRAPPER_CLASS = "loop-points-wrapper";
  const POINTS_CLASS = "loop-points-display";

  /** Parse a price string like "$65.99" or "65.99" → float */
  function parsePrice(text) {
    if (!text) return null;
    const num = parseFloat(text.replace(/[^0-9.]/g, ""));
    return isNaN(num) ? null : num;
  }

  /**
   * Given a purchase-option element, find its displayed (active) price.
   * Looks for:
   *   1. A sale/current price when a compare-at price is present
   *   2. Falls back to any price element inside the option
   *
   * Selector names are based on common Loop widget markup — adjust if yours differ.
   */
  function getActivePrice(optionEl) {
    // Loop typically marks the sale price with one of these:
    const saleCandidates = [
      ".loop-widget-purchase-option-price--sale",
      ".loop-widget-purchase-option-current-price",
      "[data-sale-price]",
    ];

    for (const sel of saleCandidates) {
      const el = optionEl.querySelector(sel);
      if (el) {
        const price = parsePrice(el.dataset.salePrice || el.textContent);
        if (price !== null) return price;
      }
    }

    // No explicit sale price – grab the first generic price element
    const genericCandidates = [
      ".loop-widget-purchase-option-price",
      "[data-price]",
      ".price",
    ];

    for (const sel of genericCandidates) {
      const el = optionEl.querySelector(sel);
      if (el) {
        const price = parsePrice(el.dataset.price || el.textContent);
        if (price !== null) return price;
      }
    }

    return null;
  }

  /** Build the points div */
  function buildPointsEl(points) {
    const div = document.createElement("div");
    div.className = POINTS_CLASS;
    div.setAttribute("aria-label", `Earn ${points} points with this purchase`);
    div.innerHTML = `
      
      <span class="${POINTS_CLASS}__text">+ ${points} points</span>
      <span class="${POINTS_CLASS}__icon" aria-hidden="true">
      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
  <path d="M9 2.62565H7.35563C7.37391 2.61019 7.39266 2.59519 7.41047 2.57878C7.55283 2.4523 7.66757 2.29781 7.7475 2.12496C7.82743 1.95211 7.87083 1.76463 7.875 1.57425C7.88116 1.36597 7.84468 1.15863 7.76781 0.964961C7.69094 0.77129 7.57529 0.595374 7.42798 0.448013C7.28066 0.300652 7.10478 0.184956 6.91113 0.108026C6.71748 0.0310973 6.51015 -0.00544106 6.30188 0.000654441C6.11141 0.00475687 5.92384 0.0481317 5.75091 0.128064C5.57798 0.207996 5.42341 0.322765 5.29688 0.465186C5.12179 0.668105 4.97945 0.897112 4.875 1.14394C4.77055 0.897112 4.62821 0.668105 4.45312 0.465186C4.32659 0.322765 4.17202 0.207996 3.99909 0.128064C3.82616 0.0481317 3.63859 0.00475687 3.44812 0.000654441C3.23985 -0.00544106 3.03252 0.0310973 2.83887 0.108026C2.64522 0.184956 2.46934 0.300652 2.32202 0.448013C2.17471 0.595374 2.05906 0.77129 1.98219 0.964961C1.90532 1.15863 1.86884 1.36597 1.875 1.57425C1.87917 1.76463 1.92257 1.95211 2.0025 2.12496C2.08243 2.29781 2.19717 2.4523 2.33953 2.57878C2.35734 2.59425 2.37609 2.60925 2.39438 2.62565H0.75C0.551088 2.62565 0.360322 2.70467 0.21967 2.84532C0.0790176 2.98598 0 3.17674 0 3.37565V4.87565C0 5.07457 0.0790176 5.26533 0.21967 5.40598C0.360322 5.54664 0.551088 5.62565 0.75 5.62565V8.62565C0.75 8.82457 0.829018 9.01533 0.96967 9.15598C1.11032 9.29664 1.30109 9.37565 1.5 9.37565H4.3125C4.36223 9.37565 4.40992 9.3559 4.44508 9.32074C4.48025 9.28557 4.5 9.23788 4.5 9.18815V4.87565H0.75V3.37565H4.5V4.87565H5.25V3.37565H9V4.87565H5.25V9.18815C5.25 9.23788 5.26975 9.28557 5.30492 9.32074C5.34008 9.3559 5.38777 9.37565 5.4375 9.37565H8.25C8.44891 9.37565 8.63968 9.29664 8.78033 9.15598C8.92098 9.01533 9 8.82457 9 8.62565V5.62565C9.19891 5.62565 9.38968 5.54664 9.53033 5.40598C9.67098 5.26533 9.75 5.07457 9.75 4.87565V3.37565C9.75 3.17674 9.67098 2.98598 9.53033 2.84532C9.38968 2.70467 9.19891 2.62565 9 2.62565ZM2.83641 2.01628C2.77084 1.9569 2.71825 1.88461 2.68195 1.80395C2.64565 1.72328 2.62642 1.63598 2.62547 1.54753C2.6231 1.44389 2.64144 1.34082 2.67941 1.24436C2.71738 1.1479 2.77422 1.05998 2.8466 0.985767C2.91898 0.911553 3.00545 0.852531 3.10093 0.812159C3.19641 0.771787 3.29899 0.750877 3.40266 0.750654H3.42563C3.51408 0.751608 3.60138 0.770839 3.68204 0.807139C3.76271 0.843439 3.835 0.896023 3.89438 0.961592C4.28766 1.40597 4.42641 2.14284 4.47516 2.59847C4.01766 2.55019 3.28125 2.41144 2.83641 2.01628ZM6.91453 2.01628C6.46969 2.41003 5.73141 2.54878 5.27391 2.59753C5.32969 2.10487 5.48438 1.38347 5.85938 0.962061C5.91875 0.896492 5.99104 0.843907 6.07171 0.807608C6.15237 0.771308 6.23967 0.752077 6.32812 0.751123H6.35109C6.45477 0.751771 6.55727 0.773105 6.65259 0.813875C6.74791 0.854645 6.83414 0.914031 6.90621 0.988552C6.97829 1.06307 7.03477 1.15123 7.07234 1.24786C7.1099 1.34449 7.12781 1.44764 7.125 1.55128C7.12347 1.63913 7.10392 1.72573 7.06756 1.80572C7.03119 1.88571 6.97879 1.95737 6.91359 2.01628H6.91453Z" fill="#8E7005"/>
</svg></span>
    `;
    return div;
  }

  /** Inject points display next to a label element */
  function injectPoints(labelEl) {
    // Skip if already processed
    if (labelEl.closest(`.${WRAPPER_CLASS}`)) return;

    // Find the nearest purchase-option container (parent of the label)
    const optionEl =
      labelEl.closest(".loop-widget-purchase-option") || labelEl.parentElement;

    const price = getActivePrice(optionEl);
    if (price === null) return; // Can't determine price; skip

    const points = Math.floor(price * POINTS_PER_DOLLAR);

    // Create wrapper, move label into it, append points div
    const wrapper = document.createElement("div");
    wrapper.className = WRAPPER_CLASS;

    labelEl.parentNode.insertBefore(wrapper, labelEl);
    wrapper.appendChild(labelEl);
    wrapper.appendChild(buildPointsEl(points));
  }

  /** Process all current labels */
  function processAll() {
    document
      .querySelectorAll(`.loop-widget-purchase-option-label:not(.${POINTS_CLASS}-done)`)
      .forEach((label) => {
        injectPoints(label);
        label.classList.add(`${POINTS_CLASS}-done`); // mark as processed
      });
  }

  /** Watch for dynamically rendered Loop widgets (they often render late) */
  function observe() {
    const observer = new MutationObserver(() => processAll());
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Run on DOM ready and keep watching
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      processAll();
      observe();
    });
  } else {
    processAll();
    observe();
  }

  /* ─── Styles (injected once) ─────────────────────────────────────────── */
  if (!document.getElementById("loop-points-styles")) {
    const style = document.createElement("style");
    style.id = "loop-points-styles";
    style.textContent = `
      .${WRAPPER_CLASS} {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .${POINTS_CLASS} {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 0.75em;
        color: #555;
        line-height: 1.2;
      }
      .${POINTS_CLASS}__icon {
        font-size: 0.9em;
      }
      .${POINTS_CLASS}__text {
        font-weight: 500;
      }
    `;
    document.head.appendChild(style);
  }
})();